import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getAttemptsResult } from '@/api/student';
import { Card } from '@/components/ui/Card';
import { PageHeader, QueryState, Stat } from '@/components/kit/ui';
import { fmtDateTime, unwrap } from '@/components/kit/data';

interface Review { question_id: number; body: string; type: string; marks: number; your_answer: unknown; your_answer_text?: string | null; score: number | null; correct: boolean | null; correct_answer: { id: number; body: string }[] | null }
interface Result { attempt_id: number; title: string; status: string; submitted_at: string | null; result_visible: boolean; message: string | null; score?: number; total_marks?: number; percent?: number | null; passed?: boolean | null; marking_pending?: boolean; review?: Review[] }

const show = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : Array.isArray(v) ? v.join(', ') : String(v));

export function TestResultPage() {
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['student-attempt-result', id], queryFn: () => getAttemptsResult(id).then((r) => unwrap<Result>(r)) });
  const r = q.data;
  return (
    <div className="space-y-4">
      <PageHeader title={r?.title ?? 'Test result'} subtitle={r?.submitted_at ? `Submitted ${fmtDateTime(r.submitted_at)}` : undefined} action={<Link to="/student/tests" className="text-sm font-semibold text-brand-600">Back to tests</Link>} />
      <QueryState q={q}>
        {r && (!r.result_visible ? (
          <Card><p className="text-sm text-slate-600">Your test was submitted. {r.message}</p></Card>
        ) : (
          <>
            <div className="grid sm:grid-cols-3 gap-3">
              <Stat label="Score" value={`${r.score} / ${r.total_marks}`} />
              <Stat label="Percentage" value={r.percent != null ? `${r.percent}%` : '—'} />
              <Stat label="Outcome" value={r.passed == null ? '—' : r.passed ? 'Passed' : 'Not passed'} tone={r.passed == null ? undefined : r.passed ? 'text-green-700' : 'text-red-700'} />
            </div>
            {r.message && <p className="text-sm text-amber-700">{r.message}</p>}
            {!!r.review?.length && (
              <Card title="Review">
                <ol className="space-y-3">
                  {r.review.map((x, i) => (
                    <li key={x.question_id} className="border rounded-xl p-3 text-sm">
                      <div className="flex justify-between gap-3"><span className="font-medium whitespace-pre-wrap">{i + 1}. {x.body}</span><span className={x.correct ? 'text-green-700' : x.correct === false ? 'text-red-700' : 'text-slate-500'}>{x.score ?? '—'} / {x.marks}</span></div>
                      <div className="text-slate-600 mt-1">Your answer: {x.your_answer_text ?? show(x.your_answer)}</div>
                      {x.correct_answer && x.correct_answer.length > 0 && <div className="text-green-700">Correct: {x.correct_answer.map((c) => c.body).join(', ')}</div>}
                    </li>
                  ))}
                </ol>
              </Card>
            )}
          </>
        ))}
      </QueryState>
    </div>
  );
}
